import { supabase } from "@/integrations/supabase/client";
import type { Profile } from "@/lib/people-constants";
import { canInviteByStatus, isOpenForCoffee } from "@/lib/people-constants";

/** Можно ли написать человеку: есть контакт и статус не «Пока только изучаю». */
export function canBeContacted(p: Profile): boolean {
  const hasTg = !!p.telegram?.trim();
  const hasLink = (p.social_links ?? []).some((l) => !!l?.trim());
  return (hasTg || hasLink) && canInviteByStatus(p.coffee_status);
}

/** Сравнение при равных интересах: мой город, фото, открыт для кофе. */
function tieBreak(a: Profile, b: Profile, myCity: string | undefined): number {
  const city = (p: Profile) => (myCity && p.city === myCity ? 1 : 0);
  const photo = (p: Profile) => (p.photo_url ? 1 : 0);
  const coffee = (p: Profile) => (isOpenForCoffee(p.coffee_status) ? 1 : 0);
  return city(b) - city(a) || photo(b) - photo(a) || coffee(b) - coffee(a);
}

/** Единый источник людей для подбора (Люди + Главная). */
export async function fetchPeople() {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      "user_id, full_name, city, district, neighborhood, photo_url, profession, industry, industries, interests, languages, about, can_help_with, looking_for, looking_for_tags, coffee_status, role_tags, social_links, telegram, age, gender, origin_city, time_in_turkey, conversation_topic, username, has_seen_welcome, visible_in_directory, visitor_kind, created_at, expert_category, expert_categories, expert_offer, expert_formats, expert_area, expert_price_from, expert_price_currency, expert_link, expert_hidden, is_blogger, blog_url, hosts_for_experts, hosts_for_experts_note",
    )
    .not("full_name", "is", null)
    .not("city", "is", null)
    .eq("visible_in_directory", true)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as Profile[];
}

/** Количество общих интересов с текущим резидентом. */
export function sharedCount(p: Profile, myInterests: Set<string>): number {
  return (p.interests ?? []).reduce(
    (acc, i) => acc + (myInterests.has(i.toLowerCase()) ? 1 : 0),
    0,
  );
}

/** Названия общих интересов (в оригинальном написании собеседника). */
export function sharedInterests(p: Profile, myInterests: Set<string>): string[] {
  return (p.interests ?? []).filter((i) => myInterests.has(i.toLowerCase()));
}

export function toInterestSet(me: Profile | null | undefined): Set<string> {
  return new Set((me?.interests ?? []).map((s) => s.toLowerCase()));
}

/** Люди с пересечением интересов, отсортированные по его величине. */
export function rankMatches(
  people: Profile[],
  myUserId: string | undefined,
  myInterests: Set<string>,
  myCity?: string | null,
): Profile[] {
  const city = myCity?.trim() || undefined;
  return people
    .filter((p) => p.user_id !== myUserId && canBeContacted(p))
    .map((p) => ({ p, s: sharedCount(p, myInterests) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || tieBreak(a.p, b.p, city))
    .map((x) => x.p);
}

/** Запасной список (без обрезки): люди из моего города, иначе просто новые. */
export function fallbackPeopleFor(
  people: Profile[],
  myUserId: string | undefined,
  myCity: string | null | undefined,
): Profile[] {
  const others = people.filter((p) => p.user_id !== myUserId && canBeContacted(p));
  const city = myCity?.trim() || undefined;
  const sameCity = city ? others.filter((p) => p.city === city) : [];
  return [...(sameCity.length > 0 ? sameCity : others)].sort((a, b) => tieBreak(a, b, city));
}

export function showcaseTitleFor(state: 1 | 2 | 3, hasMatches: boolean, hasCity: boolean): string {
  if (state === 3) return "Подобрал для тебя";
  if (state === 2) return hasMatches ? "Рекомендации по интересам" : "Люди из твоего города";
  return hasCity ? "Люди из твоего города" : "Новые участники рядом";
}

/** Минимум полей для подбора по совпадению (Люди, ARENA). */
export type MatchShape = {
  interests?: string[] | null;
  looking_for_tags?: string[] | null;
  can_help_with?: string | null;
  industry?: string | null;
  industries?: string[] | null;
  profession?: string | null;
};

const lowerSet = (xs: (string | null | undefined)[]) =>
  new Set(xs.filter(Boolean).map((s) => s!.trim().toLowerCase()));

/**
 * Совпадение двух людей: интересы, «кого ищу», «что могу», индустрия.
 * Возвращает вес и до двух коротких причин «почему вам стоит познакомиться».
 */
export function matchScore(
  me: MatchShape | null | undefined,
  p: MatchShape,
  opts: { arena?: boolean } = {},
): {
  score: number;
  reasons: { kind: "interests" | "looking" | "help" | "industry"; text: string }[];
} {
  const reasons: { kind: "interests" | "looking" | "help" | "industry"; text: string }[] = [];
  let score = 0;
  const mine = lowerSet(me?.interests ?? []);
  const common = (p.interests ?? []).filter((i) => mine.has(i.toLowerCase()));
  if (common.length) {
    score += common.length * 2;
    reasons.push({ kind: "interests", text: common.slice(0, 3).join(", ").toLowerCase() });
  }
  // ARENA: теги целей не учитываются — главный сигнал там свободный текст.
  const myTags = lowerSet(opts.arena ? [] : (me?.looking_for_tags ?? []));
  const tagHits = (p.looking_for_tags ?? []).filter((t) => myTags.has(t.toLowerCase())).length;
  if (tagHits) {
    score += tagHits * 2;
    reasons.push({ kind: "looking", text: "" });
  }
  const myInd = lowerSet([me?.industry, ...(me?.industries ?? [])]);
  const ind = [p.industry, ...(p.industries ?? [])].find((x) => x && myInd.has(x.toLowerCase()));
  if (ind) {
    score += 2;
    reasons.push({ kind: "industry", text: ind });
  }
  const myProf = me?.profession?.trim().toLowerCase();
  if (opts.arena && myProf && p.profession?.trim().toLowerCase() === myProf) {
    score += 1;
    reasons.push({ kind: "industry", text: p.profession!.trim() });
  }
  const help = p.can_help_with?.trim();
  if (help && myTags.size > 0) {
    score += 1;
    reasons.push({ kind: "help", text: help.length > 40 ? `${help.slice(0, 40)}…` : help });
  }
  return { score, reasons: reasons.slice(0, 2) };
}
